import { useMemo } from 'react'
import { useTable } from 'react-table'

import {
  Icon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  TablePagination,
  useMediaQuery,
  Skeleton,
} from '@mui/material'

const AdxTableV2 = ({
  data,
  column,
  nav,
  onClick,
  rowClickable,
  sx,
  sxHeader,
  sxCell,
  sxNav,
  stickyHeader,
  stickyFirstColumn,
  allColumnSorted = true,
  pagination = false,
  count,
  page,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  cellBorder = false,
  height,
  pushScrollBarToEnd,
  highlightHeaderValue,
  isLoading,
  headerCentered,
  highlightWhenHovered,
  ...rest
}) => {
  const isLarge = useMediaQuery((theme) => theme.breakpoints.up('lg'))

  const tableHeader = useMemo(() => column, [column])
  const tableInstance = useTable({
    columns: tableHeader,
    data: data,
    sortTypes: {
      alphanumeric: (prev, curr, columnId) => {
        const prevValue = prev?.original[columnId]
        const currValue = curr?.original[columnId]

        if (typeof prevValue === 'number' && typeof currValue === 'number') {
          // Compare numeric values
          return prevValue - currValue
        } else {
          // Compare string values (case-insensitive)
          const prevString = prevValue?.toString().toLowerCase() || ''
          const currString = currValue?.toString().toLowerCase() || ''
          if (prevString > currString) {
            return 1
          } else if (prevString < currString) {
            return -1
          } else {
            return 0
          }
        }
      },
    },
  })
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance

  return (
    <Box>
      <TableContainer
        className="scrollbar"
        sx={{
          maxHeight: height,
          backgroundColor: 'white.main',
          boxShadow: 'none',
          // '& th.MuiTableCell-root': { zIndex: 100 },
          '& td.MuiTableCell-root': {
            padding: '0px',
          },
          ...sx,
        }}
        {...rest}
      >
        <Table stickyHeader={stickyHeader} {...getTableProps}>
          <TableHead>
            {headerGroups.map((headerGroup) => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, index) =>
                  index === 0 ? (
                    <TableCell
                      key={index}
                      align="left"
                      sx={{
                        zIndex: 49,
                        position: stickyFirstColumn ? 'sticky' : 'static',
                        left: stickyFirstColumn ? 0 : 'auto',
                        bgcolor: 'gray-background.main',
                        ...(cellBorder === true && {
                          borderRight: '1px solid',
                          borderColor: 'gray-line.main',
                        }),
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: 1,
                          alignItems: 'center',
                          justifyContent: allColumnSorted ? 'space-between' : 'start',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            minHeight: '25px',
                            maxHeight: '25px',
                            color: 'light-gray-text.main',
                            display: 'flex',
                            alignItems: 'center',
                            ...sxHeader,
                          }}
                        >
                          {isLoading ? (
                            <Skeleton width={'200px'}></Skeleton>
                          ) : (
                            column.render('Header')
                          )}
                        </Typography>
                        {allColumnSorted && (
                          <>
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <Icon sx={{ fontSize: '18px', color: 'alternate.main' }}>
                                  arrow_drop_up
                                </Icon>
                              ) : (
                                <Icon sx={{ fontSize: '18px', color: 'alternate.main' }}>
                                  arrow_drop_down
                                </Icon>
                              )
                            ) : (
                              <Icon
                                sx={{
                                  fontSize: '18px',
                                  color: 'gray-inactive-button.main',
                                }}
                              >
                                arrow_drop_down
                              </Icon>
                            )}
                          </>
                        )}
                      </Box>
                    </TableCell>
                  ) : (
                    <TableCell
                      key={index}
                      align="center"
                      sx={{
                        bgcolor:
                          highlightHeaderValue === column.render('Header')
                            ? 'primary.main'
                            : 'gray-background.main',

                        ...(cellBorder === true &&
                          index !== 0 && {
                            borderRight: '1px solid',
                            borderColor: 'gray-line.main',
                          }),
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: 1,
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            width: allColumnSorted === true ? '' : '100%',
                            minHeight: '25px',
                            maxHeight: '25px',
                            color:
                              highlightHeaderValue === column.render('Header')
                                ? 'primary.contrastText'
                                : 'light-gray-text.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: headerCentered === true ? 'center' : 'start',
                            ...sxHeader,
                          }}
                        >
                          {isLoading ? (
                            <Skeleton width={'50px'}></Skeleton>
                          ) : (
                            column.render('Header')
                          )}
                        </Typography>
                        {allColumnSorted && (
                          <>
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <Icon sx={{ fontSize: '18px', color: 'alternate.main' }}>
                                  arrow_drop_up
                                </Icon>
                              ) : (
                                <Icon sx={{ fontSize: '18px', color: 'alternate.main' }}>
                                  arrow_drop_down
                                </Icon>
                              )
                            ) : (
                              <Icon
                                sx={{
                                  fontSize: '18px',
                                  color: 'gray-inactive-button.main',
                                }}
                              >
                                arrow_drop_down
                              </Icon>
                            )}
                          </>
                        )}
                      </Box>
                    </TableCell>
                  )
                )}
              </TableRow>
            ))}
          </TableHead>
          <TableBody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row)
              return (
                <TableRow
                  {...row.getRowProps()}
                  onClick={rowClickable ? () => onClick(row.original) : () => {}}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: highlightWhenHovered && 'gray-menu-hover.main' },
                  }}
                >
                  {row.cells.map((cell, index) => {
                    return index === 0 ? (
                      <TableCell
                        align="left"
                        {...cell.getCellProps()}
                        sx={{
                          bgcolor: 'inherit',
                          position: stickyFirstColumn ? 'sticky' : 'static',
                          left: stickyFirstColumn ? 0 : 'auto',
                          zIndex: 48,
                          ...(cellBorder === true && {
                            borderRight: '1px solid',
                            borderColor: 'gray-line.main',
                          }),
                        }}
                      >
                        {isLoading ? (
                          <Box sx={{ p: 2 }}>
                            <Skeleton width={'auto'}></Skeleton>
                            <Skeleton width={'100px'}></Skeleton>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              textAlign: index === 0 && 'left',
                              ...sxCell,
                            }}
                          >
                            {cell.render('Cell')}
                          </Box>
                        )}
                      </TableCell>
                    ) : (
                      <TableCell
                        align="left"
                        {...cell.getCellProps()}
                        sx={{
                          zIndex: 48,
                          ...(cellBorder === true &&
                            index !== 0 && {
                              borderRight: '1px solid',
                              borderColor: 'gray-line.main',
                            }),
                        }}
                      >
                        {isLoading ? (
                          <Box sx={{ p: 2 }}>
                            <Skeleton width={'auto'}></Skeleton>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              textAlign: index === 0 && 'left',
                              ...sxCell,
                            }}
                          >
                            {cell.render('Cell')}
                          </Box>
                        )}
                      </TableCell>
                    )
                  })}
                  {/* Dummy cell to push the scrollbar to the side. enable pushScrollBarToEnd in props to enable it. Only use it when the table container width is not set to 100% */}
                  {pushScrollBarToEnd && (
                    <TableCell
                      sx={{ width: '100vw', borderBottom: 'none', bgcolor: 'white.main' }}
                    />
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && (
        <TablePagination
          component="div"
          labelRowsPerPage={isLarge ? 'Show' : null}
          SelectProps={{ sx: { borderRadius: '4.5px' } }}
          showFirstButton
          showLastButton
          rowsPerPageOptions={[5, 10, 20, 30, 40, 50]}
          count={count}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          sx={{ borderTop: '1px solid ', borderColor: 'gray-line.main' }}
        />
      )}
    </Box>
  )
}

export default AdxTableV2
